/* eslint-disable react/no-array-index-key */
import React from 'react';

import {
  Heading,
  Paragraph,
  List,
  ListItem,
  TableRow,
  Text,
  Link,
  Container,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import {
  StyledTable,
  StyledTableCol,
} from 'client/components/basicTable/BasicTable.styled';
import { TaggedAccordion } from 'client/components/taggedAccordion/TaggedAccordion';
import testIds from 'client/testIds';

import BackToTopLink from './BackToTopLink';
import { LimitedCompanyCriteriaContent } from '../LimitedCompanyCriteria.config';

function IncomeSection() {
  const { income, backToTopLabel, jumpToASection } =
    useContent<LimitedCompanyCriteriaContent>();

  const {
    id,
    title,
    affordabilityAndSustainability,
    directorsOrShareholdersNotPaidInSterling,
    contractors,
    incomeTypes,
    lengthOfEmploymentSelfEmployment,
    rentalIncome,
    secondJob,
    selfEmployedDirectorsOrShareholders,
    selfEmployedContractors,
    taxOnRentalIncome,
  } = income;

  return (
    <section id={id} data-testid={testIds.limitedCompanyCriteria.incomeSection}>
      <Heading
        size="s5"
        marginBottom="05"
        marginTop="05"
        as="h2"
        dataTestId={testIds.limitedCompanyCriteria.incomeHeading}
      >
        {title}
      </Heading>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {affordabilityAndSustainability.title}
          </Heading>
        }
        id={affordabilityAndSustainability.id}
        variation="inpage1"
        dataTestId={
          testIds.limitedCompanyCriteria.affordabilityAndSustainability
        }
      >
        {affordabilityAndSustainability.paragraphs.map((item, idx) => (
          <Paragraph key={`affordability-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {affordabilityAndSustainability.someInfoWeMightNeed.title}
        </Heading>
        <List marginBottom="05">
          {affordabilityAndSustainability.someInfoWeMightNeed.listItems.map(
            (item, idx) => (
              <ListItem key={`affordability-info-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {affordabilityAndSustainability.learnMoreAbout.title}
        </Heading>
        {affordabilityAndSustainability.learnMoreAbout.links.map(
          (link, idx) => (
            <Paragraph marginBottom="03" key={`affordability-link-${idx}`}>
              <Link href={link.url}>{link.label}</Link>
            </Paragraph>
          ),
        )}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {directorsOrShareholdersNotPaidInSterling.title}
          </Heading>
        }
        id={directorsOrShareholdersNotPaidInSterling.id}
        variation="inpage1"
        dataTestId={
          testIds.limitedCompanyCriteria
            .directorsOrShareholdersNotPaidInSterling
        }
      >
        {directorsOrShareholdersNotPaidInSterling.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`directors-sterling-${idx}`}>{item}</Paragraph>
          ),
        )}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {contractors.title}
          </Heading>
        }
        id={contractors.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.contractors}
      >
        {contractors.paragraphs.map((item, idx) => (
          <Paragraph key={`contractors-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {contractors.subcontractors.title}
        </Heading>
        {contractors.subcontractors.paragraphs.map((item, idx) => (
          <Paragraph key={`subcontractors-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {contractors.itAndOtherContractors.title}
        </Heading>
        {contractors.itAndOtherContractors.paragraphs.map((item, idx) => (
          <Paragraph key={`it-contractors-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {contractors.constructionIndustryScheme.title}
        </Heading>
        {contractors.constructionIndustryScheme.paragraphs.map((item, idx) => (
          <Paragraph key={`cis-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {contractors.zeroHoursContracts.title}
        </Heading>
        {contractors.zeroHoursContracts.paragraphs.map((item, idx) => (
          <Paragraph key={`zero-hours-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {contractors.professionalSportsPeople.title}
        </Heading>
        {contractors.professionalSportsPeople.paragraphs.map((item, idx) => (
          <Paragraph key={`sports-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {contractors.additionalInformation.title}
        </Heading>
        {contractors.additionalInformation.links.map((link, idx) => (
          <Paragraph
            marginBottom="03"
            key={`contractors-additional-link-${idx}`}
          >
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {incomeTypes.title}
          </Heading>
        }
        id={incomeTypes.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.incomeTypes}
      >
        <Paragraph>{incomeTypes.paragraph1}</Paragraph>
        <List>
          {incomeTypes.listItems.map((item, idx) => (
            <ListItem key={`income-type-item-${idx}`}>{item}</ListItem>
          ))}
        </List>

        <Container padding="none" marginBottom="05">
          <StyledTable headings={incomeTypes.table.headings}>
            {incomeTypes.table.rows.map((row, rowIdx) => (
              <TableRow key={`income-type-row-${rowIdx}`}>
                {row.map((el, colIdx) => (
                  <StyledTableCol
                    heading={incomeTypes.table.headings[colIdx]}
                    data-testid={`income-types-${rowIdx}-${colIdx}`}
                    key={`income-types-${rowIdx}-${colIdx}`}
                  >
                    <Text>{el}</Text>
                  </StyledTableCol>
                ))}
              </TableRow>
            ))}
          </StyledTable>
        </Container>

        <Heading as="h4" size="s2">
          {incomeTypes.additionalInfo.title}
        </Heading>
        {incomeTypes.additionalInfo.links.map((link, idx) => (
          <Paragraph
            marginBottom="03"
            key={`income-type-additional-link-${idx}`}
          >
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {lengthOfEmploymentSelfEmployment.title}
          </Heading>
        }
        id={lengthOfEmploymentSelfEmployment.id}
        variation="inpage1"
        dataTestId={
          testIds.limitedCompanyCriteria.lengthOfEmploymentSelfEmployment
        }
      >
        {lengthOfEmploymentSelfEmployment.paragraphs.map((item, idx) => (
          <Paragraph key={`length-employment-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {lengthOfEmploymentSelfEmployment.additionalInfo.title}
        </Heading>
        {lengthOfEmploymentSelfEmployment.additionalInfo.links.map(
          (link, idx) => (
            <Paragraph marginBottom="03" key={`length-employment-link-${idx}`}>
              <Link href={link.url}>{link.label}</Link>
            </Paragraph>
          ),
        )}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {rentalIncome.title}
          </Heading>
        }
        id={rentalIncome.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.rentalIncome}
      >
        {rentalIncome.paragraph}
        <Heading as="h4" size="s2">
          {rentalIncome.toKeyADirector.title}
        </Heading>
        {rentalIncome.toKeyADirector.paragraphs.map((item, idx) => (
          <Paragraph key={`rental-to-director-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {rentalIncome.forAllRemortgages.title}
        </Heading>
        {rentalIncome.forAllRemortgages.paragraphs.map((item, idx) => (
          <Paragraph key={`rental-remortgage-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {rentalIncome.forBuyToLet.title}
        </Heading>
        {rentalIncome.forBuyToLet.paragraphs.map((item, idx) => (
          <Paragraph key={`rental-buytolet-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {rentalIncome.additionalInfo.title}
        </Heading>
        {rentalIncome.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="03" key={`rental-additional-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {secondJob.title}
          </Heading>
        }
        id={secondJob.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.secondJob}
      >
        <Paragraph>{secondJob.paragraph1}</Paragraph>
        <List marginBottom="05">
          {secondJob.listItems.map((item, idx) => (
            <ListItem key={`second-job-item-${idx}`}>{item}</ListItem>
          ))}
        </List>
        <Paragraph>{secondJob.paragraph2}</Paragraph>

        <Heading as="h4" size="s2">
          {secondJob.additionalInfo.title}
        </Heading>
        {secondJob.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="03" key={`second-job-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {selfEmployedDirectorsOrShareholders.title}
          </Heading>
        }
        id={selfEmployedDirectorsOrShareholders.id}
        variation="inpage1"
        dataTestId={
          testIds.limitedCompanyCriteria.selfEmployedDirectorsOrShareholders
        }
      >
        {selfEmployedDirectorsOrShareholders.paragraphs.map((item, idx) => (
          <Paragraph key={`self-employed-director-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {selfEmployedDirectorsOrShareholders.ifAnyOfTheFollowingApply.title}
        </Heading>
        <List marginBottom="05">
          {selfEmployedDirectorsOrShareholders.ifAnyOfTheFollowingApply.listItems.map(
            (item, idx) => (
              <ListItem key={`self-employed-director-apply-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>
        <Paragraph>
          {
            selfEmployedDirectorsOrShareholders.ifAnyOfTheFollowingApply
              .paragraph
          }
        </Paragraph>

        <Heading as="h4" size="s2">
          {selfEmployedDirectorsOrShareholders.theNetProfitField.title}
        </Heading>
        <List marginBottom="05">
          {selfEmployedDirectorsOrShareholders.theNetProfitField.listItems.map(
            (item, idx) => (
              <ListItem key={`self-employed-director-netprofit-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>

        <Heading as="h4" size="s2">
          {selfEmployedDirectorsOrShareholders.documentationNeeded.title}
        </Heading>
        <List marginBottom="05">
          {selfEmployedDirectorsOrShareholders.documentationNeeded.listItems.map(
            (item, idx) => (
              <ListItem key={`self-employed-director-docs-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>

        <Heading as="h4" size="s2">
          {
            selfEmployedDirectorsOrShareholders.taxCalculationsAreAvailable
              .title
          }
        </Heading>
        <List marginBottom="05">
          {selfEmployedDirectorsOrShareholders.taxCalculationsAreAvailable.listItems.map(
            (item, idx) => (
              <ListItem key={`self-employed-director-taxcalc-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>

        <Heading as="h4" size="s2">
          {selfEmployedDirectorsOrShareholders.onlineTaxAssessments.title}
        </Heading>
        <List marginBottom="05">
          {selfEmployedDirectorsOrShareholders.onlineTaxAssessments.listItems.map(
            (item, idx) => (
              <ListItem key={`self-employed-director-onlinetax-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>
        {selfEmployedDirectorsOrShareholders.onlineTaxAssessments.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`self-employed-director-onlinetax-${idx}`}>
              {item}
            </Paragraph>
          ),
        )}

        <Heading as="h4" size="s2">
          {selfEmployedDirectorsOrShareholders.additionalInfo.title}
        </Heading>

        {selfEmployedDirectorsOrShareholders.additionalInfo.links.map(
          (link, idx) => (
            <Paragraph
              marginBottom="02"
              key={`self-employed-director-additional-link-${idx}`}
            >
              <Link href={link.url}>{link.label}</Link>
            </Paragraph>
          ),
        )}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {selfEmployedContractors.title}
          </Heading>
        }
        id={selfEmployedContractors.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.selfEmployedContractors}
      >
        <Container padding="none" marginBottom="05">
          <StyledTable headings={selfEmployedContractors.table.headings}>
            {selfEmployedContractors.table.rows.map((row, rowIdx) => (
              <TableRow key={`self-employed-contractors-${rowIdx}`}>
                {row.map((el, idx) => (
                  <StyledTableCol
                    heading={selfEmployedContractors.table.headings[idx]}
                    key={`self-employed-contractors-${rowIdx}-${idx}`}
                    data-testid={`${testIds.limitedCompanyCriteria.selfEmployedContractors}-${rowIdx}-${idx}`}
                  >
                    {typeof el === 'string' ? <Text>{el}</Text> : el}
                  </StyledTableCol>
                ))}
              </TableRow>
            ))}
          </StyledTable>
        </Container>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {taxOnRentalIncome.title}
          </Heading>
        }
        id={taxOnRentalIncome.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.taxOnRentalIncome}
      >
        {taxOnRentalIncome.paragraphs.map((item, idx) => (
          <Paragraph key={`tax-rental-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2">
          {taxOnRentalIncome.additionalInfo.title}
        </Heading>
        {taxOnRentalIncome.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`tax-rental-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <BackToTopLink
        dataTestId={`${id}-back-to-top`}
        jumpToASectionId={jumpToASection.id}
      >
        {backToTopLabel}
      </BackToTopLink>
    </section>
  );
}

export default IncomeSection;
